import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ListResponse, ProductDto } from "@reina/shared";
import type { ClassificationInput } from "@reina/shared";
import { api } from "./client";

const ProductList = ListResponse(ProductDto);

export type ProductListParams = {
  search?: string;
  brandId?: string;
  domain?: string;
  needsReview?: boolean;
  limit?: number;
  offset?: number;
};

/** Products list (`GET /api/products`) with the contract's search filters. */
export function useProducts(params: ProductListParams = { limit: 500 }) {
  return useQuery({
    queryKey: ["products", "list", params],
    queryFn: () => api.get("/products", { query: params, schema: ProductList }),
  });
}

/**
 * `PATCH /api/products/:id/classification` — human correction; the server also
 * records a classification_overrides row so re-ingests apply it for free.
 */
export function useUpdateClassification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ClassificationInput }) =>
      api.patch(`/products/${id}/classification`, input, { schema: ProductDto }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["products"] }),
  });
}

/** `POST /api/products/ai-classify {limit}` response. */
const AiClassifyResult = z.object({
  processed: z.number().int(),
  updated: z.number().int(),
});
export type AiClassifyResult = z.infer<typeof AiClassifyResult>;

/** AI classification pass over rows the deterministic classifier couldn't place. */
export function useAiClassify() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (limit: number) =>
      api.post("/products/ai-classify", { limit }, { schema: AiClassifyResult }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["products"] });
      void queryClient.invalidateQueries({ queryKey: ["ai-jobs"] });
    },
  });
}
